import {
    useGetApartmentMutation,
    usePostApartmentsMutation,
    usePutApartmentsMutation
} from '@services/member/apartment/apartment.endpoints';
import { ApartmentToCreate } from '@services/member/apartment/apartment.types';
import { PriceType } from '@services/member/list/price-types/price-types.types';
import { useGetPriceTypesMutation } from '@services/member/list/price-types/price-types.endpoints';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const useAddPageVM = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [apartment, setApartment] = useState<ApartmentToCreate>();
    const [priceTypes, setPriceTypes] = useState<PriceType[]>([]);
    const [selectedPriceType, setSelectedPriceType] = useState<PriceType | null>(null);

    const [
        getPriceTypes,
        {
            isLoading: isGetPriceTypesLoading,
            isError: isGetPriceTypesError,
            error: getPriceTypesError,
            isSuccess: isGetPriceTypesSuccess,
            data: getPriceTypesResponse
        }
    ] = useGetPriceTypesMutation();

    const [
        getApartment,
        {
            isLoading: isGetApartmentLoading,
            isError: isGetApartmentError,
            error: getApartmentError,
            isSuccess: isGetApartmentSuccess,
            data: getApartmentResponse
        }
    ] = useGetApartmentMutation();

    const [
        createApartment,
        {
            isLoading: isCreateApartmentLoading,
            isError: isCreateApartmentError,
            isSuccess: isCreateApartmentSuccess,
            error: createApartmentError,
            data: createApartmentResponse
        }
    ] = usePostApartmentsMutation();

    const [
        updateApartment,
        {
            isLoading: isUpdateApartmentLoading,
            isError: isUpdateApartmentError,
            error: updateApartmentError,
            isSuccess: isUpdateApartmentSuccess,
            data: updateApartmentResponse
        }
    ] = usePutApartmentsMutation();

    useEffect(() => {
        if (!id) return;
        getApartment({ id });
    }, [id]);

    useEffect(() => {
        getPriceTypes();
    }, []);

    useEffect(() => {
        if (isUpdateApartmentLoading) return;
        if (isUpdateApartmentError) return;
        if (updateApartmentError) return;

        if (isUpdateApartmentSuccess && updateApartmentResponse) {
            navigate(`/members-management/apartments/${updateApartmentResponse.data.id}/details`);
        }
    }, [
        isUpdateApartmentError,
        isUpdateApartmentSuccess,
        updateApartmentError,
        updateApartmentResponse,
        isUpdateApartmentLoading,
        navigate
    ]);

    useEffect(() => {
        if (isCreateApartmentLoading) return;
        if (isCreateApartmentError) return;
        if (createApartmentError) return;

        if (isCreateApartmentSuccess && createApartmentResponse) {
            navigate(`/members-management/apartments/${createApartmentResponse.data.id}/details`);
        }
    }, [
        isCreateApartmentError,
        isCreateApartmentSuccess,
        createApartmentError,
        createApartmentResponse,
        isCreateApartmentLoading,
        navigate
    ]);

    useEffect(() => {
        if (isGetApartmentLoading) return;
        if (isGetApartmentError) return;
        if (getApartmentError) return;

        if (isGetApartmentSuccess && getApartmentResponse) {
            setApartment(getApartmentResponse.data);
        }
    }, [isGetApartmentError, isGetApartmentSuccess, getApartmentError, getApartmentResponse, isGetApartmentLoading]);

    useEffect(() => {
        if (isGetPriceTypesLoading) return;
        if (isGetPriceTypesError && getPriceTypesError) return;

        if (isGetPriceTypesSuccess && getPriceTypesResponse) {
            setPriceTypes(getPriceTypesResponse.data);
            setSelectedPriceType(getPriceTypesResponse.data[0] || null);
        }
    }, [isGetPriceTypesError, isGetPriceTypesSuccess, getPriceTypesError, getPriceTypesResponse, isGetPriceTypesLoading]);

    const submitForm = async (values: ApartmentToCreate) => {
        if (apartment && id) {
            await updateApartment({ id: id, apartment: values });
        } else {
            await createApartment(values);
        }
    };

    return {
        apartment,
        isEdit: !!id,
        submitForm,
        isLoading: isCreateApartmentLoading || isGetApartmentLoading || isUpdateApartmentLoading,
        priceTypes,
        selectedPriceType,
        setSelectedPriceType
    };
};
